import { ContentPage } from "@/components/organisms";

export const PrivacyPolicyPage = () => {
  const pageHeader = "Privacy Policy";

  const paragraphObject = [
    {
      header: "Privacy Policy",
      paragraph: `Last updated March 18, 2025

This Privacy Notice for Khanh Mai LLC ("we," "us," or "our") describes how and why we might access, collect, store, use, and/or share ("process") your personal information when you use our services ("Services"), including when you:
- Visit our website at https://pharmdsinit.com, or any website of ours that links to this Privacy Notice
- Use PharmDs in IT. PharmDs in IT is a job search platform designed specifically for healthcare professionals seeking careers in healthcare technology. The platform aggregates job postings from various application-tracking systems, categorizes them based on healthcare domains, and provides advanced filtering options to help users find relevant opportunities efficiently.
- Engage with us in other related ways, including any sales, marketing, or events

Questions or concerns? Reading this Privacy Notice will help you understand your privacy rights and choices. We are responsible for making decisions about how your personal information is processed. If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at support@pharmdsinit.com.`,
    },
    {
      header: "Summary of Key Points",
      paragraph: `This summary provides key points from our Privacy Notice, but you can find out more details about any of these topics by clicking the link following each key point or by using our table of contents below to find the section you are looking for.

- What personal information do we process? When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the Services, the choices you make, and the products and features you use. Learn more about personal information you disclose to us.
- Do we process any sensitive personal information? Some of the information may be considered "special" or "sensitive" in certain jurisdictions (for example, your racial or ethnic origins, sexual orientation, and religious beliefs). We may process sensitive personal information when necessary with your consent or as otherwise permitted by applicable law. Learn more about sensitive information we process.
- Do we collect any information from third parties? We do not collect any information from third parties.
- How do we process your information? We process your information to provide, improve, and administer our Services, communicate with you, ensure security and fraud prevention, and comply with the law. We may also process your information for other purposes with your consent. Learn more about how we process your information.
- In what situations and with which types of parties do we share personal information? We may share information in specific situations and with specific categories of third parties. Learn more about when and with whom we share your personal information.
- How do we keep your information safe? We have adequate organizational and technical processes and procedures in place to protect your personal information. However, no electronic transmission or storage technology can be guaranteed to be 100% secure. Learn more about how we keep your information safe.
- What are your rights? Depending on where you are located, applicable privacy laws may provide you with certain rights regarding your personal information. Learn more about your privacy rights.
- How do you exercise your rights? The easiest way to exercise your rights is by visiting http://www.pharmdsinit.com/contact, or by contacting us. We will consider and act upon any request in accordance with applicable data protection laws.
- Want to learn more about what we do with any information we collect? Review the Privacy Notice in full.`,
    },
    {
      header: "Table of Contents",
      paragraph: `1. What Information Do We Collect?
2. How Do We Process Your Information?
3. What Legal Bases Do We Rely On To Process Your Personal Information?
4. When And With Whom Do We Share Your Personal Information?
5. What Is Our Stance On Third-Party Websites?
6. Do We Use Cookies And Other Tracking Technologies?
7. How Do We Handle Your Social Logins?
8. How Long Do We Keep Your Information?
9. How Do We Keep Your Information Safe?
10. Do We Collect Information From Minors?
11. What Are Your Privacy Rights?
12. Controls For Do-Not-Track Features
13. Do United States Residents Have Specific Privacy Rights?
14. Do Other Regions Have Specific Privacy Rights?
15. Do We Make Updates To This Notice?
16. How Can You Contact Us About This Notice?
17. How Can You Review, Update, Or Delete The Data We Collect From You?`,
    },
    {
      header: "1. What Information Do We Collect?",
      paragraph: `Personal Information You Disclose To Us
In Short: We collect personal information that you provide to us.

We collect personal information that you voluntarily provide when you register on our Services, express interest in our products and Services, participate in activities on our Services, or otherwise contact us.

Personal Information Provided by You: The personal information we collect depends on your interactions with us and the Services, the choices you make, and the products and features you use. This may include:
- Names
- Email addresses
- Job titles
- Passwords
- Contact or authentication data

Sensitive Information: When necessary, with your consent or as otherwise permitted by law, we process the following sensitive information:
- Student data

Payment Data: If you make purchases, we may collect data necessary to process your payment (e.g., your payment instrument number and security code). All payment data is handled and stored by Stripe (see https://stripe.com/privacy).

Social Media Login Data: If you register using your social media account (e.g., Facebook or X), we will collect certain profile information as described in "How Do We Handle Your Social Logins?".

All personal information provided must be true, complete, and accurate, and you must notify us of any changes.

Information Automatically Collected
In Short: Some information, such as your IP address and browser/device characteristics, is collected automatically.

We automatically collect technical and usage data (e.g., IP address, browser type, device information, operating system, language preferences, referring URLs, device name, country, location, and usage data) to maintain the security and operation of our Services, and for analytics and reporting.

This also includes:
- Log and Usage Data: Service-related diagnostic and performance information.
- Device Data: Information about your computer, phone, tablet, or other devices.
- Location Data: Data about your device's location (precise or imprecise), which you can opt out of by disabling location settings.
- Google API: Our use of data from Google APIs adheres to the Google API Services User Data Policy.`,
    },
    {
      header: "2. How Do We Process Your Information?",
      paragraph: `In Short: We process your information to provide, improve, and administer our Services, communicate with you, ensure security, prevent fraud, and comply with legal obligations. We may also process your data for other purposes with your consent.

We use your personal information to:
- Facilitate account creation and management
- Deliver and support our Services
- Respond to inquiries and provide customer support
- Request feedback and communicate regarding your usage
- Send marketing and promotional communications (if opted in)
- Deliver targeted advertising based on your interests and location
- Ensure security and prevent fraud
- Analyze usage trends to improve our Services
- Protect your vital interests when necessary`,
    },
    {
      header: "3. What Legal Bases Do We Rely On To Process Your Information?",
      paragraph: `In Short: We only process your personal information when we have a valid legal basis, such as your consent, to comply with legal obligations, to provide services, to protect your rights, or for our legitimate interests.

For users in the EU or UK, we rely on:
- Consent: With your permission (withdrawable at any time)
- Performance of a Contract: To provide our Services or prior to forming a contract with you
- Legitimate Interests: For business purposes (provided these do not outweigh your rights)
- Legal Obligations: To comply with laws or cooperate with law enforcement
- Vital Interests: To protect someone’s life or safety

For users in Canada, similar principles apply, and in exceptional cases we may process data without consent as permitted by law.`,
    },
    {
      header: "4. When And With Whom Do We Share Your Personal Information?",
      paragraph: `In Short: We may share your information with specific third parties in certain situations.

We may share your data with:
- Vendors, consultants, and other third-party service providers (e.g., ad networks, data analytics services, payment processors, data storage service providers, performance monitoring tools, sales & marketing tools, social networks, and website hosting service providers)

Additionally, your personal information may be shared in the event of:
- Business Transfers: In mergers, asset sales, financings, or acquisitions, your information may be transferred to the new entity.`,
    },
    {
      header: "5. What Is Our Stance On Third-Party Websites?",
      paragraph: `In Short: We are not responsible for the safety of any information you share with third parties via links on our Services.

Our Services may link to third-party websites, online services, or mobile applications and may include third-party advertisements. We do not guarantee the safety or privacy practices of these external sites. Please review their privacy policies directly.`,
    },
    {
      header: "6. Do We Use Cookies And Other Tracking Technologies?",
      paragraph: `In Short: We use cookies and similar tracking technologies to collect and store information.

We use cookies, web beacons, and pixels to:
- Maintain the security of our Services and your account
- Prevent crashes and fix bugs
- Save your preferences and support basic site functions
- Track usage to improve user experience
- Serve personalized advertising based on your interests

Third parties may also use tracking technologies for analytics and advertising. Specific details (and opt-out options) are provided in our Cookie Notice.

Google Analytics: We may share your data with Google Analytics for analysis. To opt out, visit https://tools.google.com/dlpage/gaoptout.`,
    },
    {
      header: "7. How Do We Handle Your Social Logins?",
      paragraph: `In Short: If you use a social media account to register or log in, we may receive certain profile information.

When you log in via a third-party (e.g., Facebook or X), we receive profile data such as:
- Your name
- Your email address
- Your profile picture

This information is used solely for authentication and enhancing your experience. Please review the social media provider's privacy policy for more details.`,
    },
    {
      header: "8. How Long Do We Keep Your Information?",
      paragraph: `In Short: We retain your personal information only as long as necessary to fulfill the purposes outlined in this Privacy Notice, unless a longer retention period is required by law.

We will keep your personal information for as long as needed for:
- Providing our Services
- Complying with legal obligations (e.g., tax or accounting requirements)
- Resolving disputes or enforcing our agreements

Once no longer necessary, your data will be deleted, anonymized, or securely stored until deletion is possible.`,
    },
    {
      header: "9. How Do We Keep Your Information Safe?",
      paragraph: `In Short: We implement organizational and technical measures to protect your personal information.

While we have implemented reasonable security measures, no method of electronic transmission or storage is 100% secure. We cannot guarantee absolute security; therefore, you should only access our Services within a secure environment.`,
    },
    {
      header: "10. Do We Collect Information From Minors?",
      paragraph: `In Short: We do not knowingly collect data from or market to children under 18 years of age.

We do not knowingly collect or solicit personal information from children under 18. By using our Services, you represent that you are at least 18 or that you are the parent/guardian of a minor and consent to such minor’s use of our Services. If we learn that data from a minor under 18 has been collected, we will take prompt steps to delete it.`,
    },
    {
      header: "11. What Are Your Privacy Rights?",
      paragraph: `In Short: Depending on your location, you may have rights regarding your personal data.

For example, in regions such as the EEA, UK, Switzerland, and Canada, you may have rights including:
- Access to your personal data
- Correction of inaccuracies
- Deletion of your data
- Restricting or objecting to processing
- Data portability
- The right not to be subject to automated decision-making

If you are in the EEA or UK and believe we are unlawfully processing your personal data, you have the right to lodge a complaint with your local data protection authority.`,
    },
    {
      header: "How to Exercise Your Rights",
      paragraph: `To exercise your privacy rights, please visit http://www.pharmdsinit.com/contact or email us at support@pharmdsinit.com. We may need additional information to verify your identity before processing your request.`,
    },
    {
      header: "12. Controls For Do-Not-Track Features",
      paragraph: `Most web browsers and some mobile devices include a Do-Not-Track ("DNT") feature. Currently, no uniform standard exists for DNT signals, so we do not currently respond to them. If a standard is adopted in the future, we will update this Privacy Notice accordingly.

California law requires that we inform you how we respond to DNT signals; however, due to the lack of an industry standard, we do not honor them at this time.`,
    },
    {
      header: "13. Do United States Residents Have Specific Privacy Rights?",
      paragraph: `In Short: Residents of certain US states (e.g., California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Tennessee, Texas, Utah, or Virginia) may have specific rights regarding their personal information.

These rights may include:
- Knowing whether we process your personal data
- Accessing, correcting, or deleting your personal data
- Opting out of processing for targeted advertising, data sale, or profiling
- Obtaining details about the categories of personal data we process and the third parties with which we share your data

Additional rights may be available depending on your state.`,
    },
    {
      header: "Categories of Personal Information We Collect",
      paragraph: `We have collected the following categories of personal information in the past twelve (12) months:

A. Identifiers (e.g., real name, alias, postal address, telephone number, unique identifiers, IP address, email address, account name): NO  
B. Personal information as defined in the California Customer Records statute (e.g., education, employment history, financial information): NO  
C. Protected classification characteristics (e.g., gender, age, date of birth, race, ethnicity, marital status): NO  
D. Commercial information (e.g., transaction information, purchase history, payment information): NO  
E. Biometric information (e.g., fingerprints, voiceprints): NO  
F. Internet or other similar network activity (e.g., browsing history, search history, online behavior): NO  
G. Geolocation data (e.g., device location): NO  
H. Audio, electronic, sensory, or similar information (e.g., images, audio, video recordings): NO  
I. Professional or employment-related information (e.g., business contact details, work history, job title): NO  
J. Education Information (e.g., student records): NO  
K. Inferences drawn from collected personal information (e.g., profiling): NO  
L. Sensitive Personal Information (e.g., account login information): YES

We only collect sensitive personal information as defined by applicable laws or with your consent, and we do not process it for the purpose of inferring characteristics about you.`,
    },
    {
      header: "Additional Personal Information",
      paragraph: `We may also collect other personal information when you interact with us in person, online, by phone, or by mail, including when you:
- Receive help through our customer support channels
- Participate in customer surveys or contests
- Engage in activities that help us deliver our Services and respond to your inquiries`,
    },
    {
      header: "Sources of Personal Information",
      paragraph: `Learn more about the sources of personal information we collect by referring to the "What Information Do We Collect?" section above.`,
    },
    {
      header: "How We Use And Share Personal Information",
      paragraph: `Learn more about how we use your personal information by referring to the "How Do We Process Your Information?" section above.

Will Your Information Be Shared With Anyone Else?

We may disclose your personal information with our service providers under written contracts. We also use your data for our own business purposes, such as internal research for technological development and demonstration. This does not constitute the "selling" of your personal information.

We have disclosed personal information to third parties for business or commercial purposes as detailed in the sections above.`,
    },
    {
      header: "14. Do Other Regions Have Specific Privacy Rights?",
      paragraph: `In Short: You may have additional rights based on your country of residence.

Australia and New Zealand: We process your personal information in accordance with Australia's Privacy Act 1988 and New Zealand's Privacy Act 2020. This notice satisfies the requirements defined in those Acts.

Republic of South Africa: You have the right to request access to or correction of your personal information. If you are unsatisfied with our handling of your data, you may contact the Information Regulator:
- General enquiries: enquiries@inforegulator.org.za  
- Complaints (complete POPIA/PAIA form 5): PAIAComplaints@inforegulator.org.za & POPIAComplaints@inforegulator.org.za`,
    },
    {
      header: "15. Do We Make Updates To This Notice?",
      paragraph: `In Short: Yes, we will update this Privacy Notice as necessary to comply with relevant laws.

We may update this Privacy Notice from time to time. The updated version will be indicated by a new "Last Updated" date at the top. If material changes are made, we may notify you by posting a notice or sending you a notification. We encourage you to review this notice frequently.`,
    },
    {
      header: "16. How Can You Contact Us About This Notice?",
      paragraph: `If you have questions or comments about this Privacy Notice, you may contact us at:
- Email: support@pharmdsinit.com`,
    },
    {
      header:
        "17. How Can You Review, Update, Or Delete The Data We Collect From You?",
      paragraph: `Based on the applicable laws of your country or state, you may have the right to request access to, update, or delete the personal information we collect from you. You may also have the right to withdraw your consent to our processing of your personal information, subject to applicable law.

To request to review, update, or delete your data, please visit http://www.pharmdsinit.com/contact or contact us at support@pharmdsinit.com. We may require additional information to verify your identity before processing your request.`,
    },
  ];

  return (
    <ContentPage
      componentName="privacy-policy"
      pageHeader={pageHeader}
      paragraphObject={paragraphObject}
    />
  );
};
